import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button, TextField, InputAdornment } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { fetchDepartmentsByBusinessUnit } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";

export default function DepartmentListPage() {
  const { businessDomainId, businessUnitId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchDepartmentsByBusinessUnit(businessUnitId);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) { notify.apiError(e); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessUnitId]);

  useEffect(() => { loadData(); }, [loadData]);

  const filteredRows = searchText
    ? rows.filter((r) => r.departmentName?.toLowerCase().includes(searchText.toLowerCase()))
    : rows;

  const columns = [
    { field: "departmentName", headerName: t("department.name"), flex: 1, minWidth: 200 },
    { field: "jobName", headerName: t("department.job"), flex: 1, minWidth: 160 },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("department.list.title")}</Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <TextField size="small" placeholder={t("common.search")} value={searchText} onChange={(e) => setSearchText(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate(`/strategy/${businessDomainId}/business-units/${businessUnitId}/departments/create`)}>{t("common.add")}</Button>
        </Box>
      </Box>
      <DataGrid
        rows={filteredRows}
        columns={columns}
        getRowId={(r) => r.departmentId}
        loading={loading}
        autoHeight
        pageSizeOptions={[10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        disableRowSelectionOnClick
        onRowClick={(params) => navigate(`/strategy/${businessDomainId}/business-units/${businessUnitId}/departments/${params.row.departmentId}`)}
        sx={{
          "& .MuiDataGrid-row": { cursor: "pointer" },
          "& .MuiDataGrid-row:hover": { backgroundColor: "action.hover" },
        }}
      />
      <Box sx={{ mt: 2 }}>
        <Button variant="outlined" onClick={() => navigate(`/strategy/${businessDomainId}/business-units/${businessUnitId}`)}>{t("common.back")}</Button>
      </Box>
    </Box>
  );
}
